<script setup lang="ts">
import TheBottomSheet from '@/components/TheBottomSheet.vue'
import CrossIcon from '@/components/icons/CrossIcon.vue'
import { computed } from 'vue'
import { DateTime } from 'luxon'
import type { WinnerNotification } from '@/types/shared/player'
import { useAuth } from '@/composables/auth'
import { getCardMedal, getPrizeTypeImage } from '@/composables/game/challenges'
import ChallengeBoardCard2 from '@/components/ChallengeBoardCard2.vue'
import { availableBadges } from '@/composables/game/badge'
import ClockIcon from '@/assets/images/gold-clock.png'
import { CHALLENGE_PRIZE_TYPE } from '@/types/shared/player-config'
import { twMerge } from 'tailwind-merge'
import StarsImage from '@/assets/images/star-group.png'

const props = defineProps<{
	show: boolean,
	notification: WinnerNotification,
	contentClass?: string,
	outerContentClass?: string,
	backgroundImage?: string,
	mainImage: string,
	mainImageClass?: string,
	mainImageBackgroundClass?: string,
	withStars?: boolean,
	timerBackgroundClass?: string,
	timerBackgroundStyle?: Record<string, string>,
	title?: string,
	description?: string,
	scoreImage?: string,
	scoreValue?: string | number,
}>()

defineEmits(['hide'])

const { profile } = useAuth()

const formattedDate = computed(() => {
  return DateTime.fromSeconds(props.notification.date).toFormat('ccc dd LLLL yyyy')
})
</script>

<template>
  <TheBottomSheet
    :show="show"
    centered
    base-modal-class="overflow-visible rounded-[32px] mt-10"
    :content-class="twMerge('overflow-visible px-0 pt-6 flex flex-col', contentClass)"
    :outer-content-class="twMerge('overflow-visible', outerContentClass)"
    border-container-class="overflow-visible"
    @hide="$emit('hide')"
  >
    <template #closeButton>
      <div
        class="absolute w-6 h-6 text-black bg-black top-0 right-0 z-3 rounded-full border-2 border-white/60 flex items-center justify-center"
        @click="$emit('hide')"
      >
        <CrossIcon class="text-white/60 [&>path]:stroke-[4px] size-[12px]" />
      </div>
    </template>
    <template #baseContent>
      <div
        class="h-0 w-full relative"
        @click="$emit('hide')"
      >
        <img
          v-if="backgroundImage"
          class="h-auto w-full absolute top-full left-1/2 -translate-x-1/2 -translate-y-1/2"
          :src="backgroundImage"
        >
        <div
          :class="twMerge('absolute -top-5 left-1/2 -translate-x-1/2 -translate-y-1/2 size-[154px] bg-[rgba(231,46,109,0.44)] blur-[36px] rounded-full z-[100] transition-opacity duration-300', mainImageBackgroundClass)"
        />
        <img
          :class="twMerge('size-[144px] absolute -top-5 left-1/2 -translate-x-1/2 -translate-y-[60px] z-[101] transition-all duration-300', mainImageClass)"
          :src="mainImage"
        >
        <img
          v-if="withStars"
          class="h-[138px] w-auto absolute top-0 left-1/2 -translate-x-1/2 -translate-y-[78px] z-[101]"
          :src="StarsImage"
        >
      </div>
    </template>
    <div class="flex flex-col items-center relative overflow-hidden pt-[80px]">
      <div class="px-6 text-center text-[36px] leading-10 font-bold gold-text mb-8 -tracking-[2px]">
        {{ title ?? 'Yay! You Won!' }}
      </div>
      <div
        :class="twMerge('-mx-6 h-7 w-[calc(100%+48px)] flex items-center justify-center drop-shadow-xl', timerBackgroundClass)"
        :style="timerBackgroundStyle"
      >
        <div class="uppercase text-white text-base font-bold text-shadow-default pr-1.5">
          Ended
        </div>
        <img
          class="size-[18px] object-cover"
          :src="ClockIcon"
          alt="Clock"
        >
        <div class="ml-1 golden-text-black-stroke text-base font-bold gold-text-shadow px-0.5">
          {{ formattedDate }}
        </div>
      </div>
      <div class="px-6 mt-6 text-center text-light-white text-base leading-5 -tracking-[0.32px] font-medium">
        {{ description }}
        <br v-if="description">
        The following reward has been sent to<br>
        your account:
      </div>

      <div class="px-6 mt-6 w-full">
        <ChallengeBoardCard2
          :player="profile"
          :index="(notification.position).toString()"
          :medal="getCardMedal(notification.position)"
          :prize-value="'amount' in notification ? notification.amount : 0"
          :prize-name="'nft' in notification ? notification.nft : ''"
          :prize-image="'lottieUrl' in notification ? notification.lottieUrl : getPrizeTypeImage(notification.prizeType, notification.specialCurrencyId)"
          :score-image="scoreImage"
          :score-value="scoreValue"
          :is-prize-pool-nft="notification.prizeType === CHALLENGE_PRIZE_TYPE.NFT"
          :is-prize-pool-badge="notification.prizeType === CHALLENGE_PRIZE_TYPE.BADGE"
          :badge="availableBadges.find((badge) => badge.id === notification.badgeId)"
          class="card"
        />
      </div>
    </div>
  </TheBottomSheet>
</template>

<style scoped lang="scss">
@import "@/assets/styles/challenges";
</style>